"use client";

import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filter, X } from "lucide-react";
import { format } from "date-fns";

export interface HistoryFilters {
  dateFrom?: string;
  dateTo?: string;
  station?: string;
  minEarnings?: number;
  maxEarnings?: number;
  rating?: number | "unrated";
  duration?: number;
}

interface HistoryFilterSheetProps {
  filters: HistoryFilters;
  onFiltersChange: (filters: HistoryFilters) => void;
  stations: string[];
}

export function HistoryFilterSheet({
  filters,
  onFiltersChange,
  stations,
}: HistoryFilterSheetProps) {
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<HistoryFilters>(filters);

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== undefined && v !== ""
  );

  const handleApply = () => {
    onFiltersChange(localFilters);
    setOpen(false);
  };

  const handleClear = () => {
    const cleared = {};
    setLocalFilters(cleared);
    onFiltersChange(cleared);
    setOpen(false);
  };

  const handleDateFromChange = (date: Date | undefined) => {
    setLocalFilters((prev) => ({
      ...prev,
      dateFrom: date ? format(date, "yyyy-MM-dd") : undefined,
    }));
  };

  const handleDateToChange = (date: Date | undefined) => {
    setLocalFilters((prev) => ({
      ...prev,
      dateTo: date ? format(date, "yyyy-MM-dd") : undefined,
    }));
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Filter className="h-4 w-4 mr-2" />
          Filter
          {hasActiveFilters && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary rounded-full" />
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-auto max-h-[80vh]">
        <SheetHeader>
          <SheetTitle>Filter Blocks</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 py-6">
          {/* Date Range */}
          <div className="space-y-3">
            <Label>Date Range</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <DatePicker
                  date={localFilters.dateFrom ? new Date(localFilters.dateFrom + "T00:00:00") : undefined}
                  onSelect={handleDateFromChange}
                />
              </div>
              <div className="flex-1">
                <DatePicker
                  date={localFilters.dateTo ? new Date(localFilters.dateTo + "T00:00:00") : undefined}
                  onSelect={handleDateToChange}
                />
              </div>
            </div>
          </div>

          {/* Station Filter */}
          {stations.length > 0 && (
            <div className="space-y-3">
              <Label>Station</Label>
              <Select
                value={localFilters.station || "all"}
                onValueChange={(v) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    station: v === "all" ? undefined : v,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All stations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stations</SelectItem>
                  {stations.map((station) => (
                    <SelectItem key={station} value={station}>
                      {station}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Earnings Range */}
          <div className="space-y-3">
            <Label>Earnings Range</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="Min ($)"
                  value={localFilters.minEarnings ? localFilters.minEarnings / 100 : ""}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      minEarnings: e.target.value
                        ? Math.round(parseFloat(e.target.value) * 100)
                        : undefined,
                    }))
                  }
                />
              </div>
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="Max ($)"
                  value={localFilters.maxEarnings ? localFilters.maxEarnings / 100 : ""}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      maxEarnings: e.target.value
                        ? Math.round(parseFloat(e.target.value) * 100)
                        : undefined,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Rating Filter */}
          <div className="space-y-3">
            <Label>Route Rating</Label>
            <Select
              value={localFilters.rating?.toString() || "all"}
              onValueChange={(v) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  rating: v === "all" ? undefined : v === "unrated" ? "unrated" : parseInt(v),
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All ratings" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ratings</SelectItem>
                <SelectItem value="unrated">Unrated</SelectItem>
                <SelectItem value="5">⭐⭐⭐⭐⭐ (5)</SelectItem>
                <SelectItem value="4">⭐⭐⭐⭐ (4+)</SelectItem>
                <SelectItem value="3">⭐⭐⭐ (3+)</SelectItem>
                <SelectItem value="2">⭐⭐ (2+)</SelectItem>
                <SelectItem value="1">⭐ (1+)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Duration Filter */}
          <div className="space-y-3">
            <Label>Block Duration</Label>
            <Select
              value={localFilters.duration?.toString() || "all"}
              onValueChange={(v) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  duration: v === "all" ? undefined : parseInt(v),
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All durations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All durations</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="150">2.5 hours</SelectItem>
                <SelectItem value="180">3 hours</SelectItem>
                <SelectItem value="210">3.5 hours</SelectItem>
                <SelectItem value="240">4 hours</SelectItem>
                <SelectItem value="270">4.5 hours</SelectItem>
                <SelectItem value="300">5 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <SheetFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClear} className="flex-1">
            <X className="h-4 w-4 mr-2" />
            Clear
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
